import { type ReactNode, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The question. Also the dialog's accessible name, so phrase it as the decision being made. */
  title: ReactNode;
  /** What the confirm actually does — consequences, scope, reversibility. */
  description?: ReactNode;
  /** The verb on the confirm button ("Delete", "Archive organization"), not "OK". */
  confirmLabel: ReactNode;
  /** Shown on the confirm button while the action is in flight. Defaults to `confirmLabel`. */
  pendingLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /** Tints the confirm button `destructive`. For actions that remove or discard something. */
  destructive?: boolean;
  /**
   * Runs on confirm. The dialog does **not** close itself — closing is the caller's, because
   * whether a failed action should leave the dialog open is the caller's decision. A returned
   * promise drives the pending state; the caller still owns reporting its failure.
   */
  onConfirm: () => void | Promise<void>;
  /** Caller-owned pending state, for actions whose in-flight flag lives outside this dialog. */
  isPending?: boolean;
  /** A third control between Cancel and confirm — a softer alternative, never a second confirm. */
  secondaryAction?: ReactNode;
  size?: "default" | "sm";
}

/**
 * The one confirmation dialog: a question, its consequences, Cancel, and one verb.
 *
 * Every destructive-action confirmation in the app renders this rather than its own
 * `AlertDialog` scaffold, so the button order, the disabled-while-pending behaviour and the
 * `alertdialog` accessible name are decided once. Anything that is not that shape — a form, a
 * choice between several outcomes, a multi-step flow — is not a confirmation and keeps its own
 * `Dialog`.
 *
 * Note this consolidates *existing* confirmations. It is not licence to add one: a risky UI
 * transition is made safe by keeping the endangered state visible, not by interrupting with a
 * dialog.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pendingLabel,
  cancelLabel = "Cancel",
  destructive,
  onConfirm,
  isPending,
  secondaryAction,
  size,
}: ConfirmDialogProps) {
  const [awaitingConfirm, setAwaitingConfirm] = useState(false);
  const pending = isPending === true || awaitingConfirm;

  const handleConfirm = () => {
    const result = onConfirm();
    if (!(result instanceof Promise)) return;
    setAwaitingConfirm(true);
    const settle = () => setAwaitingConfirm(false);
    result.then(settle, settle);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent size={size}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description === undefined || description === null ? null : (
            // A `div` rather than the default `p`, so a description may carry a list or its own
            // block markup without nesting invalid HTML inside a paragraph.
            <AlertDialogDescription render={<div />}>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
          {secondaryAction}
          <AlertDialogAction
            variant={destructive === true ? "destructive" : "default"}
            disabled={pending}
            onClick={handleConfirm}
          >
            {pending && pendingLabel !== undefined ? pendingLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
